import { useEffect, useState } from "react";
import api, { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { AppIcon } from "@/components/AppIcon";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, X, Tag, EyeOff, Inbox, Handshake } from "lucide-react";

export default function SellerPanel() {
  const { t } = useLang();
  const [listings, setListings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState({});

  const load = async () => {
    const [mine, incoming] = await Promise.all([
      api.get("/apps/mine"),
      api.get("/purchase-requests/incoming"),
    ]);
    setListings(mine.data.filter((a) => a.published));
    setRequests(incoming.data);
  };
  useEffect(() => { load(); }, []);

  const savePrice = async (id) => {
    await api.post(`/apps/${id}/price`, { price: Number(editing[id]) });
    toast.success(t("toast.priceUpdated"));
    setEditing((e) => { const n = { ...e }; delete n[id]; return n; });
    load();
  };

  const removeListing = async (id) => {
    await api.post(`/apps/${id}/unpublish`);
    toast.success(t("toast.removed"));
    load();
  };

  const act = async (req, action) => {
    await api.post(`/purchase-requests/${req.id}/${action}`);
    toast.success(action === "approve" ? t("toast.approved") : t("toast.rejected"));
    load();
  };

  const pending = requests.filter((r) => r.status === "PENDING");

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono text-xs text-primary tracking-[0.3em] mb-2">{t("seller.tag")}</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl">{t("seller.title")}</h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Inbox className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold text-lg">{t("seller.buyerRequests")}</h2>
            {pending.length > 0 && (
              <Badge className="rounded-none bg-primary/10 text-primary border-primary/50 font-mono">{pending.length} {t("seller.pending")}</Badge>
            )}
          </div>
          {requests.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center font-mono text-sm text-muted-foreground" data-testid="no-requests">
              {t("seller.noRequests")}
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <motion.div key={r.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  data-testid={`request-${r.id}`} className="border border-border bg-[#0C0D14] p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.app_name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {r.buyer_name} · {r.price > 0 ? `€${r.price}` : t("common.free")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.status === "PENDING" ? (
                      <>
                        <Button data-testid={`approve-${r.id}`} size="sm" onClick={() => act(r, "approve")}
                          className="rounded-none bg-[#00FF66] text-black font-mono text-xs uppercase hover:bg-[#00FF66]/90">
                          <Check className="w-3.5 h-3.5 mr-1" /> {t("seller.approve")}
                        </Button>
                        <Button data-testid={`reject-${r.id}`} size="sm" variant="outline" onClick={() => act(r, "reject")}
                          className="rounded-none border-[#FF2A2A]/50 text-[#FF2A2A] font-mono text-xs uppercase hover:bg-[#FF2A2A]/10">
                          <X className="w-3.5 h-3.5 mr-1" /> {t("seller.reject")}
                        </Button>
                      </>
                    ) : (
                      <Badge className={`rounded-none font-mono text-[10px] ${r.status === "APPROVED" ? "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/50" : "bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/50"}`} variant="outline">
                        {r.status === "APPROVED" ? t("seller.approve") : t("seller.reject")}
                      </Badge>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2 mb-4">
            <Handshake className="w-4 h-4 text-primary" />
            <h2 className="font-display font-bold text-lg">{t("seller.yourListings")}</h2>
          </div>
          {listings.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center font-mono text-sm text-muted-foreground" data-testid="no-listings">
              {t("seller.noListings")}
            </div>
          ) : (
            <div className="space-y-3">
              {listings.map((app) => (
                <div key={app.id} data-testid={`listing-${app.id}`} className="border border-border bg-[#0C0D14] p-4">
                  <div className="flex items-center gap-3">
                    <AppIcon name={app.name} category={app.category} src={mediaUrl(app.icon_id)} size={44} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{app.name}</span>
                        <StatusBadge status={app.status} governance={app.governance_status} />
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">{app.category} · v{app.version}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    {editing[app.id] !== undefined ? (
                      <>
                        <Input type="number" min="0" value={editing[app.id]} data-testid={`price-input-${app.id}`}
                          onChange={(e) => setEditing({ ...editing, [app.id]: e.target.value })}
                          className="rounded-none bg-[#05050A] border-border h-9 w-28 focus-visible:ring-primary" />
                        <Button size="sm" data-testid={`save-price-${app.id}`} onClick={() => savePrice(app.id)}
                          className="rounded-none bg-primary text-primary-foreground font-mono text-xs uppercase">{t("common.save")}</Button>
                      </>
                    ) : (
                      <>
                        <span className={`font-mono font-bold text-lg mr-auto ${app.price > 0 ? "text-primary" : "text-[#00FF66]"}`}>
                          {app.price > 0 ? `€${app.price}` : t("common.free")}
                        </span>
                        <Button size="sm" variant="outline" data-testid={`edit-price-${app.id}`}
                          onClick={() => setEditing({ ...editing, [app.id]: app.price })}
                          className="rounded-none border-border font-mono text-xs uppercase hover:border-primary/50 hover:text-primary">
                          <Tag className="w-3.5 h-3.5 mr-1" /> {t("seller.editPrice")}
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`remove-listing-${app.id}`} onClick={() => removeListing(app.id)}
                          className="rounded-none border-border font-mono text-xs uppercase text-muted-foreground hover:border-[#FF2A2A]/50 hover:text-[#FF2A2A]">
                          <EyeOff className="w-3.5 h-3.5 mr-1" /> {t("seller.remove")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
